// ============================================================================
// RHFormsEntrevistaDesligamento_Questionario.js
// Pesquisa de Desligamento reformulada (padrão Clima, nota por seção).
// Preenchimento INTERNO pelo RH/gestor logado. Grava na tabela nova via
// POST /api/forms-rh/desligamento/submit. A estrutura (seções + perguntas)
// vem de desligamento-perguntas.js (fonte única, espelha o backend).
//
// Cabeçalho: só Loja / Nome / Data ficam visíveis. Matrícula, CPF e Cargo são
// auto-preenchidos (por Loja→Nome) e GRAVADOS, mas totalmente ocultos na tela
// (decisão Julia 2026-07-08).
// ============================================================================

import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { Info } from 'lucide-react';
import { SECOES, ESCALA } from './desligamento-perguntas';

const COR = '#e11d48';

// IDs das perguntas obrigatórias = todas menos as de texto livre (seção 8).
const IDS_OBRIGATORIOS = SECOES.flatMap((s) =>
  s.perguntas.filter((p) => p.tipo !== 'texto').map((p) => p.id)
);
// Estado inicial das respostas: uma chave P_N vazia para cada pergunta.
const RESPOSTAS_INICIAIS = SECOES.reduce((acc, s) => {
  s.perguntas.forEach((p) => { acc[p.id] = ''; });
  return acc;
}, {});

// ---------------------------------------------------------------------------
// Renderizadores por tipo de pergunta.
//
// ⚠️ Estes componentes MORAM AQUI FORA de propósito. Quando eram declarados
// dentro do componente-pai, o React criava uma função NOVA a cada render — e,
// para o React, "tipo de componente diferente" = desmonta a árvore antiga e
// monta outra do zero. Na prática: a cada tecla digitada o <textarea> era
// destruído e recriado, o foco se perdia e o TECLADO VIRTUAL DO CELULAR
// FECHAVA. Declarados no escopo do módulo, a identidade é estável, o React só
// atualiza o valor e o foco (e o teclado) permanece.
// Regra geral: componente nunca se define dentro de outro componente.
// ---------------------------------------------------------------------------

const EscalaPergunta = ({ id, numero, texto, valor, onChange }) => (
  <div className="question-group" style={{ marginBottom: '22px' }}>
    <label className="question-label" style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>
      {numero}. {texto}
    </label>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
      {ESCALA.map((opcao, idx) => {
        const nota = 5 - idx; // Sempre=5 ... Nunca=1
        const isSelected = valor === opcao;
        return (
          <Fragment key={`${id}-${opcao}`}>
            <input
              type="radio" id={`${id}-${opcao}`} name={id} value={opcao}
              checked={isSelected} onChange={() => onChange(id, opcao)}
              required style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
            />
            <label
              htmlFor={`${id}-${opcao}`}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '2px', padding: '10px 4px', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${isSelected ? COR : '#e2e8f0'}`,
                backgroundColor: isSelected ? COR : '#fff',
                color: isSelected ? '#fff' : '#475569', transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: 800 }}>{nota}</span>
              <span style={{ fontSize: '11px', fontWeight: 600, textAlign: 'center', lineHeight: 1.1 }}>{opcao}</span>
            </label>
          </Fragment>
        );
      })}
    </div>
  </div>
);

const SelectPergunta = ({ id, numero, texto, opcoes, valor, onChange }) => (
  <div className="question-group" style={{ marginBottom: '22px' }}>
    <label htmlFor={id} className="question-label" style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
      {numero}. {texto}
    </label>
    <select id={id} name={id} value={valor} onChange={(e) => onChange(id, e.target.value)} className="form-input" required>
      <option value="">Selecione uma opção</option>
      {opcoes.map((op) => <option key={op} value={op}>{op}</option>)}
    </select>
  </div>
);

const TextoPergunta = ({ id, numero, texto, valor, onChange }) => (
  <div className="question-group" style={{ marginBottom: '22px' }}>
    <label htmlFor={id} className="question-label" style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
      {numero}. {texto} <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '13px' }}>(opcional)</span>
    </label>
    <textarea
      id={id} name={id} value={valor} onChange={(e) => onChange(id, e.target.value)}
      placeholder="Digite a sua resposta" className="form-input" rows="4"
      style={{ width: '100%', minWidth: '100%', boxSizing: 'border-box' }}
    />
  </div>
);

function EntrevistaDesligamento_Questionario({ embedded = false }) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

  const getMarginLeft = () => {
    if (window.innerWidth <= 480) return isCollapsed ? '70px' : '0px';
    return isCollapsed ? '100px' : '260px';
  };

  // --- Cabeçalho ---
  const hoje = new Date();
  const dataAtual = String(hoje.getDate()).padStart(2, '0') + '/' +
                    String(hoje.getMonth() + 1).padStart(2, '0') + '/' +
                    hoje.getFullYear();

  const [formData, setFormData] = useState({
    loja: '',
    data: dataAtual,       // auto (hoje), readOnly
    nome: '',
    // Ocultos na tela, gravados no banco:
    nomeCargo: '',
    cpf: '',
    id_funcionario: '',
  });

  // --- Dados da API ---
  const [funcionariosPorLoja, setFuncionariosPorLoja] = useState({});
  const [funcionariosAtivos, setFuncionariosAtivos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Respostas do questionário (chaveadas por P_N) ---
  const [respostas, setRespostas] = useState(RESPOSTAS_INICIAIS);

  const handleResposta = (id, value) => setRespostas((prev) => ({ ...prev, [id]: value }));

  // --- Busca de funcionários (reaproveita o picker do módulo legado) ---
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return navigate('/login');

    fetch('/api/forms-rh/entrevistad/funcionarios', { headers: { 'Authorization': `Bearer ${token}` } })
      .then((res) => { if (!res.ok) throw new Error(`Erro: ${res.status}`); return res.json(); })
      .then((funcData) => setFuncionariosPorLoja(funcData))
      .catch((err) => console.error('Falha no carregamento:', err));
  }, [navigate]);

  // --- Filtro por loja + auto-preenchimento de matrícula/CPF/cargo (ocultos) ---
  useEffect(() => {
    if (!funcionariosPorLoja || Object.keys(funcionariosPorLoja).length === 0) return;

    let lista = [];
    if (formData.loja && funcionariosPorLoja[formData.loja]) {
      lista = [...funcionariosPorLoja[formData.loja]];
    } else {
      Object.values(funcionariosPorLoja).forEach((l) => {
        if (Array.isArray(l)) l.forEach((obj) => { if (obj && obj.nome) lista.push(obj); });
      });
    }

    const listaOrdenada = lista.sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
    setFuncionariosAtivos(listaOrdenada);

    const selecionado = listaOrdenada.find((f) => f.nome === formData.nome);
    if (selecionado) {
      setFormData((prev) => ({
        ...prev,
        id_funcionario: selecionado.id_funcionario,
        cpf: selecionado.cpf,
        nomeCargo: selecionado.cargo,
      }));
    }
  }, [formData.loja, formData.nome, funcionariosPorLoja]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'loja') {
        newData.nome = '';
        newData.cpf = '';
        newData.id_funcionario = '';
        newData.nomeCargo = '';
      }
      return newData;
    });
  };

  // Numeração sequencial das perguntas dentro de cada seção (1..N por seção).
  const renderSecoes = () => SECOES.map((secao) => (
    <div key={secao.id} style={{ marginBottom: '28px' }}>
      <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
        {secao.id}. {secao.titulo}
      </h3>
      {secao.pontua && (
        <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 14px 0' }}>
          Escala: 5-Excelente · 4-Bom · 3-Regular · 2-Ruim · 1-Péssimo
        </p>
      )}
      <div style={{ marginTop: secao.pontua ? 0 : '12px' }}>
        {secao.perguntas.map((p, idx) => {
          const numero = idx + 1;
          if (p.tipo === 'escala') return <EscalaPergunta key={p.id} id={p.id} numero={numero} texto={p.texto} valor={respostas[p.id]} onChange={handleResposta} />;
          if (p.tipo === 'texto') return <TextoPergunta key={p.id} id={p.id} numero={numero} texto={p.texto} valor={respostas[p.id]} onChange={handleResposta} />;
          return <SelectPergunta key={p.id} id={p.id} numero={numero} texto={p.texto} opcoes={p.opcoes} valor={respostas[p.id]} onChange={handleResposta} />;
        })}
      </div>
    </div>
  ));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validação: cabeçalho + todas as perguntas obrigatórias (menos texto livre).
    if (!formData.loja || !formData.nome) {
      alert('Selecione a loja e o nome do colaborador.');
      return;
    }
    const faltando = IDS_OBRIGATORIOS.some((id) => !respostas[id]);
    if (faltando) {
      alert('Responda todas as perguntas obrigatórias antes de enviar.');
      return;
    }

    setIsSubmitting(true);

    const payload = {
      LOJA: formData.loja,
      DATA: formData.data,
      NOME_FUNCIONARIO: formData.nome,
      CPF: formData.cpf,
      ID_FUNCIONARIO: formData.id_funcionario,
      NOME_CARGO: formData.nomeCargo,
      ...respostas, // P_1..P_17
    };

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch('/api/forms-rh/desligamento/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) navigate('/agradecimento');
      else { alert('Erro ao enviar.'); setIsSubmitting(false); }
    } catch (err) { alert('Erro de conexão.'); setIsSubmitting(false); }
  };

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        {!embedded && (
          <div style={{ height: '80px', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#2a0f0f' }}>Preenchimento de Questionário</h2>
          </div>
        )}

        <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>

            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: '800', color: COR, marginBottom: '10px' }}>Entrevista de Desligamento</h1>
              <p style={{ color: '#64748b' }}>Avaliação da experiência do colaborador em processo de desligamento.</p>
            </div>

            <div style={{ backgroundColor: '#fff1f2', borderLeft: `4px solid ${COR}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '30px' }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#be123c', fontSize: '15px', fontWeight: '700', margin: '0 0 10px 0' }}>
                <Info size={18} /> Importante
              </h4>
              <p style={{ color: '#951d1d', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Este questionário ajuda a identificar áreas de melhoria e aperfeiçoar nossas práticas de gestão de pessoas.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="form-moderno">
              {/* Cabeçalho — só Loja / Nome / Data (identificação oculta é auto-preenchida) */}
              <div className="form-group">
                <label htmlFor="loja" className="label">Loja</label>
                <select id="loja" name="loja" value={formData.loja} onChange={handleFormChange} className="form-input" required aria-label="Selecione a loja">
                  <option value="">Selecione a Loja</option>
                  {Object.keys(funcionariosPorLoja).sort().map((loja) => (
                    <option key={loja} value={loja}>{loja}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="label">Nome</label>
                <select name="nome" value={formData.nome} onChange={handleFormChange} className="form-input" required>
                  <option value="">Selecione o nome</option>
                  {funcionariosAtivos.map((f) => <option key={f.id_funcionario} value={f.nome}>{f.nome}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="data" className="label">Data</label>
                <input id="data" type="text" name="data" value={formData.data} className="form-input" readOnly style={{ backgroundColor: '#eee' }} />
              </div>

              <div style={{ height: '1px', backgroundColor: '#f1f5f9', margin: '28px 0' }} />

              {renderSecoes()}

              <button type="submit" disabled={isSubmitting} style={{ width: '100%', padding: '16px', backgroundColor: COR, color: '#fff', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: isSubmitting ? 'not-allowed' : 'pointer', marginTop: '20px', opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting ? 'Enviando...' : 'Finalizar e Enviar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EntrevistaDesligamento_Questionario;
